import { alpha, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar, FormProvider, RHFTextField } from '@zeta-core';
import { PlusIcon, SearchIcon } from '@zeta-core/assets';
import { AdvisorAPIs, getErrorMessage } from '@zeta-services';
import { IAdvisorUser, IGenericErrorResponse, IGetAdvisorUserListRequest, IGetAdvisorUserListResponse, IRemoveAdvisorUserRequest } from '@zeta-types';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { UserDetailsTable } from './user-table';

interface DeletedUserInfo {
  user: IAdvisorUser;
  timeoutId: NodeJS.Timeout;
}

export const ManageUserAccessModal = ({
  open,
  onClose,
  handleShowAddAccess,
  userList,
  isPending,
  setAdvisorUserList,
  canWrite = false,
}: {
  open: boolean;
  onClose: () => void;
  handleShowAddAccess: () => void;
  userList: IAdvisorUser[];
  isPending: boolean;
  setAdvisorUserList: (userList: IAdvisorUser[]) => void;
  canWrite: boolean;
}) => {
  const theme = useTheme();
  const methods = useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deletedUsers, setDeletedUsers] = useState<Map<string, DeletedUserInfo>>(new Map());
  const location = useLocation();
  const caseID = location.state?.clientData?.caseID;

  const filteredUsers =
    userList?.filter((user) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (user.fullName?.toLowerCase() || '').includes(searchLower) || (user.email?.toLowerCase() || '').includes(searchLower);
    }) || [];

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const searchValue = methods.watch('search');
  useEffect(() => {
    setSearchTerm(searchValue || '');
  }, [searchValue]);

  const getAddedAdvisorUserList = useMutation({
    mutationFn: (payload: IGetAdvisorUserListRequest) => AdvisorAPIs().getAdvisorUserList(payload),
    onSuccess: (response: AxiosResponse<IGetAdvisorUserListResponse>) => {
      setAdvisorUserList(response?.data?.advisorUsers);
    },
    onError: (error: IGenericErrorResponse) => {
      console.log(error);
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const removeUserAccess = useMutation({
    mutationFn: (payload: IRemoveAdvisorUserRequest) => AdvisorAPIs().removeAdvisorUser(payload),
    onSuccess: (_response, variables) => {
      // enqueueSnackbar('User access removed successfully', { variant: 'success' });
      setDeletedUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(variables.advisorUserID);
        return newMap;
      });
      getAddedAdvisorUserList.mutate({ caseID });
    },
    onError: (error: IGenericErrorResponse, variables) => {
      const errorResponse = error?.response?.data;
      const errorCode = errorResponse?.error?.errorCode || (Array.isArray(errorResponse?.error) && errorResponse?.error[0]?.errorCode);
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });

      const deletedUserInfo = deletedUsers.get(variables.advisorUserID);
      if (deletedUserInfo) {
        setAdvisorUserList([...userList, deletedUserInfo.user]);

        setDeletedUsers((prev) => {
          const newMap = new Map(prev);
          newMap.delete(variables.advisorUserID);
          return newMap;
        });
      }
    },
  });

  const handleDeleteUser = (userDetail: IAdvisorUser) => {
    if (deletedUsers.has(userDetail.advisorUserID)) {
      // Clear existing timeout
      clearTimeout(deletedUsers.get(userDetail.advisorUserID)!.timeoutId);
    }

    setAdvisorUserList(userList.filter((user) => user.advisorUserID !== userDetail.advisorUserID));

    const timeoutId = setTimeout(() => {
      const payload: IRemoveAdvisorUserRequest = {
        caseID: caseID,
        advisorUserID: userDetail.advisorUserID,
      };

      removeUserAccess.mutate(payload);
    }, 5000);

    setDeletedUsers((prev) => {
      const newMap = new Map(prev);
      newMap.set(userDetail.advisorUserID, { user: userDetail, timeoutId });
      return newMap;
    });
  };

  const handleUndo = (advisorUserID: string) => {
    const deletedUserInfo = deletedUsers.get(advisorUserID);

    if (deletedUserInfo) {
      clearTimeout(deletedUserInfo.timeoutId);

      setAdvisorUserList([...userList, deletedUserInfo.user]);

      setDeletedUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(advisorUserID);
        return newMap;
      });
    }
  };

  const cleanupTimeouts = () => {
    deletedUsers.forEach((info) => {
      clearTimeout(info.timeoutId);
    });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={() => {
        cleanupTimeouts();
        onClose();
      }}
    >
      <DialogTitle id="caution">Manage Access</DialogTitle>

      <DialogContent>
        <Stack gap={2} sx={{ border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`, borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} mt={2} px={2}>
            <Typography variant="subtitle1" color="text.secondary">
              Manage Client Details Access
            </Typography>
            <Stack flexDirection="row" gap={2}>
              <FormProvider methods={methods}>
                <RHFTextField name="search" label="Search" placeholder="Search by name or email" size="small" InputProps={{ endAdornment: <SearchIcon /> }} />
              </FormProvider>
              <Button
                variant="soft"
                color="primary"
                disabled={!canWrite}
                onClick={() => {
                  handleShowAddAccess();
                  onClose();
                }}
              >
                <PlusIcon />
                Add Access
              </Button>
            </Stack>
          </Stack>
          <Stack>
            <UserDetailsTable
              isPending={isPending}
              tableData={paginatedUsers}
              handleDeleteUser={handleDeleteUser}
              deletedUser={deletedUsers}
              handleUndo={handleUndo}
              canWrite={canWrite}
              pagination={{
                page,
                rowsPerPage,
                count: filteredUsers.length,
                onPageChange: handlePageChange,
                onRowsPerPageChange: handleRowsPerPageChange,
                rowsPerPageOptions: [5, 10, 25],
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            cleanupTimeouts();
            onClose();
          }}
          id="cancel-button"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
